import { useEffect, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  LayoutList,
  Loader2,
  Plus,
  Search,
  SlidersHorizontal,
  ArrowUpDown,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { AppLayout } from "@/components/layout/AppLayout";
import { CategoryTree } from "../components/CategoryTree";
import { DropdownItemRow } from "../components/DropdownItemRow";
import { AddItemModal } from "../components/AddItemModal";
import {
  useCategoryItems,
  useDropdownTree,
  useTreeMetadata,
  useUpdateCategory,
} from "../hooks/useDropdowns";
import type { CategoryMeta } from "../types";

type Tab = "values" | "used-in";
type ActiveFilter = "all" | "active" | "inactive";

const CARD_BORDER = "hsl(210,30%,85%)";

export default function DropdownManagerPage() {
  const { data: tree = [] } = useDropdownTree();
  const { data: treeMetadata = [] } = useTreeMetadata();
  const [selected, setSelected] = useState<CategoryMeta | null>(null);
  const [tab, setTab] = useState<Tab>("values");
  const [configOpen, setConfigOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>("all");
  const [parentValueFilter, setParentValueFilter] = useState<string>("");
  const [valPage, setValPage] = useState(1);
  const [valLimit, setValLimit] = useState(10);

  // Auto-select first dropdown
  useEffect(() => {
    if (!selected && tree.length > 0) {
      const first = tree[0]?.pages[0]?.dropdowns[0];
      if (first) setSelected(first);
    }
  }, [tree, selected]);

  // Refresh selected meta when tree updates (item count etc)
  useEffect(() => {
    if (!selected) return;
    for (const m of tree) {
      for (const p of m.pages) {
        for (const d of p.dropdowns) {
          if (d.category === selected.category) {
            if (
              d.itemCount !== selected.itemCount ||
              d.allowMultiple !== selected.allowMultiple ||
              d.name !== selected.name ||
              d.parentCategory !== selected.parentCategory
            ) {
              setSelected(d);
            }
            return;
          }
        }
      }
    }
  }, [tree, selected]);

  const parentItems = useCategoryItems(
    selected?.parentCategory ?? undefined,
  ).data;

  const { data: items = [], isLoading, isError } = useCategoryItems(
    selected?.category,
    parentValueFilter || undefined,
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return items.filter((it) => {
      if (it.value === "__placeholder__") return false;
      if (activeFilter === "active" && !it.isActive) return false;
      if (activeFilter === "inactive" && it.isActive) return false;
      if (
        term &&
        !it.label.toLowerCase().includes(term) &&
        !it.value.toLowerCase().includes(term)
      )
        return false;
      return true;
    });
  }, [items, search, activeFilter]);

  const realCount = items.filter((i) => i.value !== "__placeholder__").length;
  const allActive = realCount > 0 && items.every(
    (i) => i.value === "__placeholder__" || i.isActive,
  );

  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / valLimit));
  const safePage = Math.min(valPage, totalPages);
  const start = total === 0 ? 0 : (safePage - 1) * valLimit + 1;
  const end = Math.min(safePage * valLimit, total);
  const visible = filtered.slice((safePage - 1) * valLimit, safePage * valLimit);

  useEffect(() => {
    setValPage(1);
  }, [search, activeFilter, parentValueFilter, selected?.category, valLimit]);

  const nextSortOrder =
    items.reduce((max, it) => Math.max(max, it.sortOrder), 0) + 1;

  const updateCategory = useUpdateCategory(selected?.category ?? "");

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Administration" },
        { label: "Dropdown Manager" },
      ]}
    >
      <div className="flex w-full flex-col gap-6">
        <div className="flex items-center gap-3">
          <LayoutList
            className="h-[18px] w-[18px] text-foreground"
            strokeWidth={1.5}
          />
          <h1 className="text-2xl font-semibold tracking-tight">
            Dropdown Manager
          </h1>
        </div>

        <div className="flex items-start gap-4">
          <CategoryTree
            selectedCategory={selected?.category ?? null}
            onSelect={(meta) => {
              setSelected(meta);
              setTab("values");
              setSearch("");
              setActiveFilter("all");
              setParentValueFilter("");
              setConfigOpen(false);
            }}
          />

          <div className="flex min-w-0 flex-1 flex-col gap-4 pr-1">
            {!selected ? (
              <div
                className="flex items-center justify-center rounded-xl border bg-card py-20 text-sm text-muted-foreground"
                style={{ borderColor: CARD_BORDER }}
              >
                Select a dropdown from the tree to view its values.
              </div>
            ) : (
              <>
                {/* SECTION 1 — Header card */}
                <div
                  className="flex items-start justify-between rounded-xl border bg-card px-5 py-4"
                  style={{ borderColor: CARD_BORDER }}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h2 className="text-xl font-bold tracking-tight">
                        {selected.name}
                      </h2>
                      {selected.isSystem && (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                          style={{
                            background: "hsl(210,55%,92%)",
                            color: "hsl(210,100%,28%)",
                          }}
                        >
                          System
                        </span>
                      )}
                      {selected.allowMultiple && (
                        <span
                          className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium"
                          style={{
                            background: "hsl(262,55%,92%)",
                            color: "hsl(262,83%,38%)",
                          }}
                        >
                          Multi-select
                        </span>
                      )}
                    </div>
                    <code className="mt-1 inline-block rounded bg-[hsl(210,30%,96%)] px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                      {selected.category}
                    </code>
                    <div className="mt-1 text-[11px] text-muted-foreground">
                      {selected.module} · {selected.page}
                    </div>
                  </div>
                  <Button onClick={() => setAddItemOpen(true)}>
                    <Plus className="h-4 w-4" strokeWidth={1.5} />
                    Add Value
                  </Button>
                </div>

                {/* SECTION 2 — Configuration card (collapsible) */}
                <div
                  className="rounded-xl border bg-card"
                  style={{ borderColor: CARD_BORDER }}
                >
                  <button
                    type="button"
                    onClick={() => setConfigOpen((v) => !v)}
                    className="flex w-full items-center justify-between px-5 py-4 text-left"
                    aria-expanded={configOpen}
                  >
                    <span className="text-[13px] font-semibold text-foreground">
                      Configuration
                    </span>
                    <ChevronRight
                      className="h-4 w-4 text-muted-foreground transition-transform"
                      strokeWidth={1.5}
                      style={{
                        transform: configOpen ? "rotate(90deg)" : "rotate(0deg)",
                      }}
                    />
                  </button>

                  {configOpen && (
                    <div
                      className="space-y-4 border-t px-5 py-4"
                      style={{ borderColor: CARD_BORDER }}
                    >
                      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        <div className="space-y-1">
                          <Label>Name</Label>
                          <div className="text-sm text-foreground">
                            {selected.name}
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label>Code</Label>
                          <code className="block w-fit rounded bg-[hsl(210,30%,96%)] px-1.5 py-0.5 font-mono text-[11px] text-muted-foreground">
                            {selected.category}
                          </code>
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="active-toggle">Active</Label>
                          <div
                            className="flex h-9 items-center gap-2"
                            title="Per-value status is managed in the Values table"
                          >
                            <input
                              id="active-toggle"
                              type="checkbox"
                              checked={allActive}
                              disabled
                              className="h-4 w-4 rounded border-border"
                            />
                            <span className="text-sm text-muted-foreground">
                              {allActive ? "All values active" : "Some inactive"}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="multi-toggle">Multi-select</Label>
                          <div className="flex h-9 items-center gap-2">
                            <input
                              id="multi-toggle"
                              type="checkbox"
                              checked={selected.allowMultiple}
                              disabled={
                                selected.isSystem || updateCategory.isPending
                              }
                              onChange={(e) =>
                                updateCategory.mutate({
                                  allowMultiple: e.target.checked,
                                })
                              }
                              className="h-4 w-4 rounded border-border"
                            />
                            <span className="text-sm">
                              {selected.allowMultiple ? "Yes" : "No"}
                            </span>
                          </div>
                        </div>
                      </div>

                      {!selected.isSystem && (
                        <div className="space-y-1">
                          <Label htmlFor="parent-select">Dependent on</Label>
                          <Select
                            id="parent-select"
                            value={selected.parentCategory ?? ""}
                            disabled={updateCategory.isPending}
                            onChange={(e) => {
                              const next = e.target.value || null;
                              if (next === selected.category) return;
                              updateCategory.mutate({ parentCategory: next });
                            }}
                          >
                            <option value="">— Independent —</option>
                            {treeMetadata
                              .filter((m) => m.category !== selected.category)
                              .map((m) => (
                                <option key={m.category} value={m.category}>
                                  {m.module} › {m.page} › {m.name}
                                </option>
                              ))}
                          </Select>
                        </div>
                      )}

                      {selected.parentCategory && (
                        <p className="text-[11px] text-muted-foreground">
                          Values are filtered based on the parent dropdown's
                          selection.
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* SECTION 3 — Values / Used In card */}
                <div
                  className="flex flex-col rounded-xl border bg-card"
                  style={{ borderColor: CARD_BORDER }}
                >
                  <div
                    className="flex border-b"
                    style={{ borderColor: CARD_BORDER }}
                  >
                    <button
                      type="button"
                      onClick={() => setTab("values")}
                      className={`-mb-px border-b-2 px-5 py-3 text-sm font-medium transition-colors ${
                        tab === "values"
                          ? "border-primary text-primary"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Values
                    </button>
                    <button
                      type="button"
                      onClick={() => setTab("used-in")}
                      className={`-mb-px border-b-2 px-5 py-3 text-sm font-medium transition-colors ${
                        tab === "used-in"
                          ? "border-primary text-primary"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      Used In
                    </button>
                  </div>

                  {tab === "values" ? (
                    <div className="flex flex-col">
                      <div
                        className="flex flex-wrap items-center gap-3 border-b px-4 py-3"
                        style={{ borderColor: CARD_BORDER }}
                      >
                        <div className="relative w-[280px]">
                          <Search
                            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                            strokeWidth={1.5}
                          />
                          <Input
                            placeholder="Search values..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9"
                          />
                        </div>
                        <Select
                          value={activeFilter}
                          onChange={(e) =>
                            setActiveFilter(e.target.value as ActiveFilter)
                          }
                          className="w-36"
                        >
                          <option value="all">All Status</option>
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </Select>
                        {selected.parentCategory && (
                          <Select
                            value={parentValueFilter}
                            onChange={(e) => setParentValueFilter(e.target.value)}
                            className="w-48"
                          >
                            <option value="">All parent values</option>
                            {(parentItems ?? [])
                              .filter((p) => p.isActive)
                              .map((p) => (
                                <option key={p.id} value={p.value}>
                                  {p.label}
                                </option>
                              ))}
                          </Select>
                        )}
                        <div className="ml-auto flex items-center gap-1">
                          <Button variant="outline" size="sm">
                            <ArrowUpDown
                              className="h-4 w-4"
                              strokeWidth={1.5}
                            />
                            Reorder
                          </Button>
                          <Button
                            variant="outline"
                            size="icon"
                            aria-label="Column settings"
                          >
                            <SlidersHorizontal
                              className="h-4 w-4"
                              strokeWidth={1.5}
                            />
                          </Button>
                        </div>
                      </div>

                      <div>
                      {isLoading ? (
                        <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
                          <Loader2
                            className="mr-2 h-4 w-4 animate-spin"
                            strokeWidth={1.5}
                          />
                          Loading values…
                        </div>
                      ) : isError ? (
                        <div className="p-6 text-sm text-destructive">
                          Failed to load values.
                        </div>
                      ) : total === 0 ? (
                        <div className="py-16 text-center text-sm text-muted-foreground">
                          No values match the current filters.
                        </div>
                      ) : (
                        <table className="w-full text-sm">
                          <thead
                            className="sticky top-0 z-10 bg-[hsl(210,30%,97%)] text-left uppercase tracking-wide text-muted-foreground"
                            style={{
                              fontSize: "11px",
                            }}
                          >
                            <tr>
                              <th className="px-4 py-3 font-medium">Label</th>
                              <th className="px-4 py-3 font-medium">Value Key</th>
                              <th className="px-4 py-3 font-medium">Sort Order</th>
                              <th className="px-4 py-3 font-medium">System</th>
                              <th className="px-4 py-3 font-medium">Status</th>
                              <th className="px-4 py-3 text-right font-medium">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-border">
                            {visible.map((item) => (
                              <DropdownItemRow
                                key={item.id}
                                item={item}
                                parentItems={
                                  selected.parentCategory
                                    ? parentItems ?? []
                                    : null
                                }
                              />
                            ))}
                          </tbody>
                        </table>
                      )}
                      </div>

                      <div
                        className="flex min-h-[44px] flex-wrap items-center justify-between gap-3 border-t bg-[hsl(210,30%,97%)] px-4 py-3 text-xs text-muted-foreground"
                        style={{ borderColor: CARD_BORDER }}
                      >
                        <div className="flex items-center gap-3">
                          <span>
                            Showing {start}-{end} of {total} entries
                          </span>
                          <div className="flex items-center gap-2">
                            <span>Show</span>
                            <Select
                              value={String(valLimit)}
                              onChange={(e) => setValLimit(Number(e.target.value))}
                              className="h-8 w-20"
                            >
                              <option value="5">5</option>
                              <option value="10">10</option>
                              <option value="25">25</option>
                              <option value="50">50</option>
                            </Select>
                            <span>entries</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="icon"
                            disabled={safePage <= 1}
                            onClick={() =>
                              setValPage((p) => Math.max(1, p - 1))
                            }
                            aria-label="Previous page"
                          >
                            <ChevronLeft
                              className="h-4 w-4"
                              strokeWidth={1.5}
                            />
                          </Button>
                          <span className="px-2 text-foreground">
                            Page {safePage} of {totalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="icon"
                            disabled={safePage >= totalPages}
                            onClick={() =>
                              setValPage((p) => Math.min(totalPages, p + 1))
                            }
                            aria-label="Next page"
                          >
                            <ChevronRight
                              className="h-4 w-4"
                              strokeWidth={1.5}
                            />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="px-6 py-16 text-center text-sm text-muted-foreground">
                      Coming soon — will show which records use this dropdown
                      value.
                    </div>
                  )}
                </div>

                <AddItemModal
                  open={addItemOpen}
                  onOpenChange={setAddItemOpen}
                  category={selected}
                  defaultSortOrder={nextSortOrder}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
